import React, { useState, useEffect } from 'react';
import { Plus, Mail, Copy, Pencil, Trash2, Eye, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import {
  getTemplates,
  deleteTemplate,
  cloneTemplate,
  DripEmailTemplate,
} from '@/actions/drip-campaigns';
import TemplateSlideForm from './TemplateSlideForm';
import TemplatePreviewModal from './TemplatePreviewModal';

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'welcome', label: 'Welcome' },
  { value: 'nurture', label: 'Nurture' },
  { value: 'educational', label: 'Educational' },
  { value: 're_engagement', label: 'Re-engagement' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'other', label: 'Other' },
];

const EmailTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<DripEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<DripEmailTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [filterCategory]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await getTemplates({
        category: filterCategory || undefined,
      });
      if (response.success && response.data) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template: DripEmailTemplate) => {
    if (template.is_system_template) {
      toast.info('System templates cannot be edited. Clone it to create your own version.');
      return;
    }
    setSelectedTemplate(template);
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (templateId: number, templateName: string, isSystem: boolean) => {
    if (isSystem) {
      toast.error('System templates cannot be deleted');
      return;
    }

    if (!confirm(`Are you sure you want to delete "${templateName}"?`)) {
      return;
    }

    try {
      await deleteTemplate(templateId);
      toast.success('Template deleted successfully');
      loadTemplates();
    } catch (error: any) {
      console.error('Error deleting template:', error);
      toast.error(error.response?.data?.error || 'Failed to delete template');
    }
  };

  const handleCloneTemplate = async (templateId: number, templateName: string) => {
    try {
      await cloneTemplate(templateId);
      toast.success(`Template "${templateName}" cloned successfully`);
      loadTemplates();
    } catch (error: any) {
      console.error('Error cloning template:', error);
      toast.error(error.response?.data?.error || 'Failed to clone template');
    }
  };

  const handlePreviewTemplate = (template: DripEmailTemplate) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const getCategoryBadge = (category: string) => {
    const badges = {
      welcome: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      nurture: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      educational: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      re_engagement: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      promotional: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      other: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
    return badges[category as keyof typeof badges] || badges.other;
  };

  const filteredTemplates = templates.filter((template) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      template.template_name.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query) ||
      template.subject_line.toLowerCase().includes(query)
    );
  });

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Email Templates"
        description="Manage reusable email templates for your drip campaigns"
        icon={<Mail className="h-5 w-5" />} />

      <div className="p-6">

      {/* Actions Bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setFilterCategory(cat.value)}
              className={`px-4 py-2 rounded-lg text-sm ${
                filterCategory === cat.value
                  ? 'bg-brandColor text-white'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="flex gap-3 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-1 md:flex-initial">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full md:w-64"
            />
          </div>

          {/* Create Button */}
          <button
            onClick={handleCreateTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover transition-colors whitespace-nowrap"
          >
            <Plus className="w-5 h-5" />
            New Template
          </button>
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading templates...</p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No templates found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery ? 'Try adjusting your search' : 'Create your first email template'}
          </p>
          {!searchQuery && (
            <button
              onClick={handleCreateTemplate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover"
            >
              <Plus className="w-5 h-5" />
              Create Template
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow"
            >
              {/* Template Header */}
              <div className="mb-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex-1 pr-2">
                    {template.template_name}
                  </h3>
                  {template.is_system_template && (
                    <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 rounded">
                      System
                    </span>
                  )}
                </div>
                <span
                  className={`inline-block px-2 py-1 text-xs font-medium rounded ${getCategoryBadge(
                    template.category
                  )}`}
                >
                  {template.category.replace('_', ' ')}
                </span>
              </div>

              {/* Description */}
              {template.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                  {template.description}
                </p>
              )}

              {/* Subject Line Preview */}
              <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Subject:</p>
                <p className="text-sm text-gray-900 dark:text-white font-medium line-clamp-1">
                  {template.subject_line}
                </p>
              </div>

              {/* Variables */}
              {template.variables && template.variables.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Variables ({template.variables.length}):
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {template.variables.slice(0, 3).map((variable) => (
                      <span
                        key={variable.name}
                        className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                      >
                        {`{{${variable.name}}}`}
                      </span>
                    ))}
                    {template.variables.length > 3 && (
                      <span className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                        +{template.variables.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handlePreviewTemplate(template)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                  title="Preview Template"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
                <button
                  onClick={() => handleCloneTemplate(template.id, template.template_name)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-brandColor text-white text-sm rounded hover:bg-brand-hover"
                  title="Clone Template"
                >
                  <Copy className="w-4 h-4" />
                  Clone
                </button>
                {!template.is_system_template && (
                  <>
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="flex items-center justify-center px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      title="Edit Template"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        handleDeleteTemplate(
                          template.id,
                          template.template_name,
                          template.is_system_template
                        )
                      }
                      className="flex items-center justify-center px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      title="Delete Template"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Template Slide Form */}
      <TemplateSlideForm
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        onSuccess={loadTemplates}
      />

      {/* Template Preview Modal */}
      {showPreviewModal && selectedTemplate && (
        <TemplatePreviewModal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
        />
      )}
      </div>
      </div>
  );
};

export default EmailTemplatesPage;
